import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  FaCalendarAlt,
  FaUser,
  FaEnvelope,
  FaBriefcase,
  FaFileAlt,
  FaClock,
  FaCheckCircle,
  FaShieldAlt,
  FaComments,
  FaCalendarCheck,
  FaGavel,
  FaBalanceScale
} from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";
import API_BASE_URL from "../config";
import "../styles/BookAppointment.css";

export default function BookAppointment() {
  const { solicitorId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const { practiceCategory, practiceArea } = location.state || {};

  const [form, setForm] = useState({
    practiceCategory: practiceCategory || "",
    practiceArea: practiceArea || "",
    caseType: "",
    caseDescription: "",
    appointmentDate: "",
    durationHours: "",
    durationMinutes: ""
  });

  // ✅ Store solicitor data
  const [solicitorData, setSolicitorData] = useState(null);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [isUnavailable, setIsUnavailable] = useState(false);
  const [ignoreAutoSave, setIgnoreAutoSave] = useState(false);

  // 🔐 Auth protection (UNCHANGED)
  useEffect(() => {
    if (!user || !token) {
      navigate("/login", {
        state: {
          redirectTo: location.pathname,
          bookingState: location.state
        }
      });
    }
  }, [user, token, navigate, location]);

  // ✅ Fetch solicitor details (UNCHANGED OPERATION)
  useEffect(() => {
    const fetchSolicitor = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/advocates/${solicitorId}`);
        const data = await res.json();

        if (res.ok) {
          setSolicitorData(data);
        }
      } catch (err) {
        console.error("Failed to fetch solicitor details", err);
      }
    };

    fetchSolicitor();
  }, [solicitorId]);


  useEffect(() => {
    const fetchBlockedSlots = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/advocates/${solicitorId}/blocked-slots`
        );

        const data = await res.json();

        if (res.ok) {
          console.log("Blocked slots for solicitor:", data.blockedSlots);
          setBlockedSlots(data.blockedSlots || []);
        } else {
          console.log("Failed to fetch blocked slots", data);
        }
      } catch (err) {
        console.error("Blocked slot fetch error", err);
      }
    };

    if (solicitorId) {
      fetchBlockedSlots();
    }
  }, [solicitorId]);

  // AUTO SAVE FORM TO SESSION STORAGE
  useEffect(() => {
    if (ignoreAutoSave) return;

    if (
      form.practiceCategory ||
      form.practiceArea ||
      form.caseType ||
      form.caseDescription
    ) {
      localStorage.setItem(
        "pendingBooking",
        JSON.stringify({
          solicitorId,
          form,
          timestamp: Date.now()
        })
      );

      // 🔥 VERY IMPORTANT
      // Reset header alert flag when new draft is created
      sessionStorage.removeItem("draftAlertShown");
    }
  }, [form, solicitorId, ignoreAutoSave]);

  useEffect(() => {
    const saved = localStorage.getItem("pendingBooking");
    if (!saved) return;

    const parsed = JSON.parse(saved);

    const now = Date.now();
    const draftAge = now - parsed.timestamp;

    const DRAFT_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours

    if (draftAge > DRAFT_EXPIRY_TIME) {
      // 🔥 Remove expired draft
      localStorage.removeItem("pendingBooking");
      sessionStorage.removeItem("draftAlertShown");
    }
  }, []);

  useEffect(() => {

    if (!user || !token) return;

    const saved = localStorage.getItem("pendingBooking");
    if (!saved) return;

    const parsed = JSON.parse(saved);

    if (parsed.solicitorId !== solicitorId) return;

    // 🔥 Make it solicitor specific
    const alertKey = `bookingAlertHandled_${solicitorId}`;

    if (sessionStorage.getItem(alertKey) === "true") {
      setForm(parsed.form); // 🔥 restore silently on refresh
      return;
    }

    if (location.state?.fromLoginRedirect) {
      setForm(parsed.form);
      return;
    }

    if (location.state?.fromHeaderDraft) {
      setForm(parsed.form);
      return;
    }

    Swal.fire({
      title: "Pending Consultation Found",
      text: "You already have pending details with this solicitor. Do you want to continue or start new?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Continue",
      cancelButtonText: "Start New",
      allowOutsideClick: false
    }).then((result) => {

      // 🔥 Store per solicitor
      sessionStorage.setItem(alertKey, "true");

      if (result.isConfirmed) {
        setForm(parsed.form);
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        setIgnoreAutoSave(true);
        localStorage.removeItem("pendingBooking");

        setForm({
          practiceCategory: practiceCategory || "",
          practiceArea: practiceArea || "",
          caseType: "",
          caseDescription: "",
          appointmentDate: "",
          durationHours: "",
          durationMinutes: ""
        });

        setTimeout(() => {
          setIgnoreAutoSave(false);
        }, 100);
      }
    });

  }, [solicitorId, location.state, user, token]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      // 🔥 Do nothing on refresh
    };

    const handleRouteLeave = () => {
      const alertKey = `bookingAlertHandled_${solicitorId}`;
      sessionStorage.removeItem(alertKey);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // 🔥 This runs when navigating to another page
      handleRouteLeave();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [solicitorId]);

  const getMinNow = () => {
    return new Date().toISOString().slice(0, 16);
  };

  const isInBlockedSlot = (date) => {

    const selected = new Date(date);
    selected.setHours(0, 0, 0, 0);

    return blockedSlots.some((slot) => {
      const start = new Date(slot.startDateTime);
      const end = new Date(slot.endDateTime);

      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      return selected >= start && selected <= end;
    });
  };



  const handleDateChange = (value) => {

    const selected = new Date(value);

    // normalize selected date
    selected.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // ✅ past date block
    if (selected < today) {
      toast.error("You cannot select past date");
      setIsUnavailable(false);
      return;
    }

    // ✅ block only blocked-slot dates (day based slot)
    if (isInBlockedSlot(selected)) {
      toast.error("This date is unavailable");
      setIsUnavailable(true);

      setForm(prev => ({
        ...prev,
        appointmentDate: ""
      }));

      return;
    }

    setIsUnavailable(false);

    setForm(prev => ({
      ...prev,
      appointmentDate: value
    }));
  };

  const handleSubmit = async () => {

    if (!form.practiceCategory) {
      toast.error("Please select practice service");
      return;
    }

    if (!form.practiceArea) {
      toast.error("Please select practice area");
      return;
    }

    if (!form.caseType) {
      toast.error("Please enter case type");
      return;
    }

    if (!form.caseDescription) {
      toast.error("Please enter case description");
      return;
    }

    if (!form.appointmentDate) {
      toast.error("Please select appointment date");
      return;
    }

    if (isSolicitorNotAvailable) {
      toast.error("This solicitor is currently not available");
      return;
    }

    const hours = parseInt(form.durationHours || 0, 10);
    const minutes = parseInt(form.durationMinutes || 0, 10);

    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 0 ||
      minutes < 0 ||
      minutes > 59 ||
      (hours === 0 && minutes === 0)
    ) {
      toast.error("Please enter valid duration");
      return;
    }

    // ✅ keep your blocked slot logic as-is (day based)
    if (isInBlockedSlot(new Date(form.appointmentDate))) {
      toast.error("Selected date is unavailable");
      return;
    }

    // ✅ only date check (no time)
    const selectedDate = new Date(form.appointmentDate);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast.error("You cannot book an appointment in the past");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/appointments/book`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          solicitorId,

          practiceCategory: form.practiceCategory,
          practiceArea: form.practiceArea,
          caseType: form.caseType,
          caseDescription: form.caseDescription,

          // ✅ only date
          appointmentDate: form.appointmentDate,

          // ✅ only hours & minutes
          durationHours: hours,
          durationMinutes: minutes
        })
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || "Failed to book appointment");
        return;
      }

      toast.success("Appointment booked successfully");
      // 🔥 REMOVE DRAFT COMPLETELY
      localStorage.removeItem("pendingBooking");



      setTimeout(() => {
        navigate("/user/dashboard", {
          state: { openAppointmentId: data.appointment._id }
        });
      }, 1500);

    } catch (err) {
      console.error(err);
      toast.error("Server error while booking appointment");
    }
  };

  const isSolicitorNotAvailable =
    solicitorData?.consultationAvailability?.status === "NOT_AVAILABLE";

  return (
    <div className="book-appointment-wrapper">
      <Header />

      <div className="book-appointment-container">
        <div className="book-appointment-header">
          <h1 className="book-appointment-title">Book Legal Consultation</h1>
          <p className="book-appointment-subtitle">
            Schedule your appointment with expert legal guidance
          </p>
        </div>

        <div className="book-appointment-layout">
          {/* LEFT SIDE - CONTENT SECTION */}
          <div className="book-appointment-content">
            <div className="book-appointment-image-container">
              <div className="book-appointment-image-placeholder">
                <img
                  src="/Home/bookapp.png"
                  alt="Legal Consultation"
                  className="book-appointment-image"
                  id="bookimg"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextElementSibling.style.display = "flex";
                  }}
                />
              </div>
            </div>

            <h3 className="book-appointment-content-title">
              <FaGavel /> Why Book With Us
            </h3>

            <ul className="book-appointment-content-list">
              <li className="book-appointment-content-item">
                <div className="book-appointment-icon-circle">
                  <FaShieldAlt size={14} />
                </div>
                <div className="book-appointment-content-text">
                  <h4>Expert Legal Guidance</h4>
                  <p>
                    Consult with experienced solicitors specializing in your
                    specific legal matter.
                  </p>
                </div>
              </li>

              <li className="book-appointment-content-item">
                <div className="book-appointment-icon-circle">
                  <FaCheckCircle size={14} />
                </div>
                <div className="book-appointment-content-text">
                  <h4>Confidential & Secure</h4>
                  <p>
                    All consultations are private and protected by
                    attorney-client privilege.
                  </p>
                </div>
              </li>

              <li className="book-appointment-content-item">
                <div className="book-appointment-icon-circle">
                  <FaComments size={14} />
                </div>
                <div className="book-appointment-content-text">
                  <h4>Clear Communication</h4>
                  <p>
                    Receive straightforward legal advice without complex
                    jargon.
                  </p>
                </div>
              </li>

              <li className="book-appointment-content-item">
                <div className="book-appointment-icon-circle">
                  <FaCalendarCheck size={14} />
                </div>
                <div className="book-appointment-content-text">
                  <h4>Flexible Scheduling</h4>
                  <p>
                    Choose appointment times that work best for your schedule.
                  </p>
                </div>
              </li>
            </ul>

            <div className="book-appointment-note">
              <p>
                <strong>Note:</strong> Please provide accurate case details to
                help our solicitors prepare for your consultation. All
                information provided is confidential.
              </p>
            </div>
          </div>

          {/* RIGHT SIDE - FORM SECTION */}
          <div className="book-appointment-form-section">
            {/* USER INFO */}
            <div className="book-appointment-user-info">
              <div className="book-appointment-user-field">
                <span className="book-appointment-user-label">
                  <FaUser size={12} /> Name
                </span>
                <span className="book-appointment-user-value">
                  {user?.name}
                </span>
              </div><br />
              <div className="book-appointment-user-field">
                <span className="book-appointment-user-label">
                  <FaEnvelope size={12} /> Email Address
                </span>
                <span className="book-appointment-user-value">
                  {user?.email}
                </span>
              </div>
            </div>

            <div className="book-appointment-form">
              {/* PRACTICE CATEGORY */}
              <div className="book-appointment-form-group">
                <label className="book-appointment-label">
                  <FaBriefcase /> Services
                </label>

                <select
                  value={form.practiceCategory}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      practiceCategory: e.target.value,
                      practiceArea: "" // reset area if category changes
                    })
                  }
                  className="book-appointment-input"
                >
                  <option value="">Select Service</option>
                  {solicitorData?.practiceAreas?.map((p, idx) => (
                    <option key={idx} value={p.category}>
                      {p.category}
                    </option>
                  ))}
                </select>
              </div>


              {/* PRACTICE AREA */}
              <div className="book-appointment-form-group">
                <label className="book-appointment-label">
                  <FaBriefcase /> Practice Area
                </label>

                <select
                  value={form.practiceArea}
                  onChange={(e) =>
                    setForm({ ...form, practiceArea: e.target.value })
                  }
                  className="book-appointment-input"
                  disabled={!form.practiceCategory}
                >
                  <option value="">Select Practice Area</option>
                  {solicitorData?.practiceAreas
                    ?.find((p) => p.category === form.practiceCategory)
                    ?.areas?.map((area, idx) => (
                      <option key={idx} value={area}>
                        {area}
                      </option>
                    ))}
                </select>
              </div>


              {/* CASE TYPE */}
              <div className="book-appointment-form-group">
                <label className="book-appointment-label">
                  Case Type{" "}
                  <span className="book-appointment-required">*</span>
                </label>
                <input
                  placeholder="e.g Divorce, Bail, Property Dispute"
                  value={form.caseType}
                  onChange={(e) =>
                    setForm({ ...form, caseType: e.target.value })
                  }
                  className="book-appointment-input"
                />
              </div>

              {/* CASE DESCRIPTION */}
              <div className="book-appointment-form-group">
                <label className="book-appointment-label">
                  <FaFileAlt /> Case Description{" "}
                  <span className="book-appointment-required">*</span>
                </label>
                <textarea
                  placeholder="Describe your case clearly with relevant details..."
                  value={form.caseDescription}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      caseDescription: e.target.value
                    })
                  }
                  className="book-appointment-textarea"
                />
              </div>

              {/* DATE & TIME */}
              <div className="book-appointment-form-group">
                <label className="book-appointment-label">
                  <FaClock /> Appointment Date & Time{" "}
                  <span className="book-appointment-required">*</span>
                </label>
                <div className="book-appointment-date-wrapper">
                  <input
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    value={form.appointmentDate}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="book-appointment-input book-appointment-date-input"
                    style={{ cursor: "pointer" }}
                  />

                </div>

                <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
                  <input
                    type="number"
                    min="0"
                    placeholder="Hours"
                    value={form.durationHours}
                    onChange={(e) =>
                      setForm({ ...form, durationHours: e.target.value })
                    }
                    className="book-appointment-input"
                  />

                  <input
                    type="number"
                    min="0"
                    max="59"
                    placeholder="Minutes"
                    value={form.durationMinutes}
                    onChange={(e) =>
                      setForm({ ...form, durationMinutes: e.target.value })
                    }
                    className="book-appointment-input"
                  />
                </div>


                {isUnavailable && (
                  <div style={{ color: "#dc2626", fontSize: "13px", marginTop: "6px" }}>
                    Unavailable for the selected date
                  </div>
                )}

                {isSolicitorNotAvailable && (
                  <div
                    style={{
                      background: "#fee2e2",
                      color: "#991b1b",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      marginBottom: "10px"
                    }}
                  >
                    This solicitor is currently not available for consultation.
                  </div>
                )}

              </div>

              <button
                onClick={handleSubmit}
                className="book-appointment-submit-btn"
                disabled={isSolicitorNotAvailable}
                style={
                  isSolicitorNotAvailable
                    ? { opacity: 0.6, cursor: "not-allowed" }
                    : {}
                }
              >
                Confirm Booking
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
